import React from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { MissionCodeType } from 'models/enums/fleet-management';
import useCodeLabel from 'hooks/useCodeLabel';
import { CODE_TONES } from 'utils/fleet-dashboard-theme';

export const CodeTooltipContent: React.FC<{ code: string; type: MissionCodeType }> = ({
  code,
  type,
}) => {
  const { title, description } = useCodeLabel()(code, type);
  return (
    <Stack gap={0.25}>
      <Typography variant="caption" fontWeight={700}>
        {title}
      </Typography>
      {description && <Typography variant="caption">{description}</Typography>}
    </Stack>
  );
};

/** A code by its localized title only; the raw code means nothing to most readers. */
const CodeChip: React.FC<{ code: string; type: MissionCodeType }> = ({ code, type }) => {
  const tone = CODE_TONES[type];
  const { title } = useCodeLabel()(code, type);
  return (
    <Tooltip title={<CodeTooltipContent code={code} type={type} />}>
      <Chip
        size="small"
        label={title}
        sx={{
          fontSize: 12,
          maxWidth: 160,
          color: tone.ink,
          backgroundColor: tone.tint,
          height: 22,
        }}
      />
    </Tooltip>
  );
};

/**
 * The codes a robot stood on at the window end, each with its localized meaning on hover. The
 * codes it reported earlier in the window but had cleared by the end sit behind one `+N`, so the
 * cell says what is wrong now without hiding what went wrong before.
 */
const CodeChips: React.FC<{
  codes: string[];
  type: MissionCodeType;
  /** Every code reported during the window; those not in `codes` are the cleared ones. */
  windowCodes?: string[];
}> = ({ codes, type, windowCodes = [] }) => {
  const { t } = useTranslation();
  const cleared = windowCodes.filter((code) => !codes.includes(code));

  if (codes.length === 0 && cleared.length === 0) {
    return (
      <Typography variant="body2" color="text.disabled">
        —
      </Typography>
    );
  }

  return (
    <Stack direction="row" gap={0.5} alignItems="center" overflow="hidden" height="100%">
      {codes.map((code) => (
        <CodeChip key={code} code={code} type={type} />
      ))}
      {cleared.length > 0 && (
        <Tooltip
          title={
            <Stack gap={1}>
              <Typography variant="caption">
                {t('fleet-management-codes-cleared-during-window')}
              </Typography>
              {cleared.map((code) => (
                <CodeTooltipContent key={code} code={code} type={type} />
              ))}
            </Stack>
          }
        >
          <Box component="span" display="inline-flex">
            <Chip
              size="small"
              variant="outlined"
              label={`+${cleared.length}`}
              aria-label={t('fleet-management-codes-cleared-count', { count: cleared.length })}
              sx={{ height: 22, fontSize: 12, color: 'text.secondary' }}
            />
          </Box>
        </Tooltip>
      )}
    </Stack>
  );
};

export default CodeChips;
